import { useElapsedSeconds } from '@shared/hooks/use-elapsed-seconds'
import { selectActiveEntry, usePollingStore } from '@shared/store/use-polling-store'
import { useMatchRoute, useNavigate } from '@tanstack/react-router'
import { Check, LoaderCircle } from 'lucide-react'
import type { PanInfo } from 'motion/react'
import { AnimatePresence, motion, useMotionValue } from 'motion/react'
import { useEffect, useState } from 'react'

const HIDDEN_ROUTES = [
  '/saju/$sajuType/result',
  '/payment',
  '/payment-success',
  '/payment-fail',
  '/oauth/callback',
  '/login',
]

export function PollingToast() {
  const elapsedSeconds = useElapsedSeconds()
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()
  const dragY = useMotionValue(0)

  const activeEntry = usePollingStore((s) => selectActiveEntry(s.entries))
  const status = activeEntry?.status || 'idle'

  const [dismissed, setDismissed] = useState(false)

  // completed로 전환되면 다시 표시
  useEffect(() => {
    if (status === 'completed') {
      setDismissed(false)
    }
  }, [status])

  const isIgnoredRoute = HIDDEN_ROUTES.some((to) => matchRoute({ to, fuzzy: true }))
  const isActive = status === 'polling' || status === 'completed'
  const visible = isActive && !dismissed && !isIgnoredRoute

  const handleNavigateToResult = () => {
    if (!activeEntry) {
      return
    }
    void navigate({
      to: '/saju/$sajuType/result',
      params: { sajuType: activeEntry.sajuType },
      search: { hashKey: activeEntry.hashKey },
    })
  }

  // 위로 스와이프 dismiss
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y < -40) {
      setDismissed(true)
    }
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="polling-toast"
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed top-3 left-1/2 z-toast -translate-x-1/2"
          drag="y"
          dragConstraints={{ top: -100, bottom: 0 }}
          dragElastic={0.4}
          onDragEnd={handleDragEnd}
          style={{ y: dragY }}
        >
          {status === 'polling' && (
            <div className="glass flex h-12 items-center gap-3 rounded-full px-4 shadow-lg">
              <div className="flex items-center gap-2">
                <LoaderCircle
                  className="size-4 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
                <span className="whitespace-nowrap typo-label font-medium text-foreground">
                  사주를 풀어보고 있어요
                </span>
              </div>
              <span className="flex items-baseline gap-0 typo-footnote text-muted-foreground">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={elapsedSeconds}
                    className="tabular-nums"
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -8, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {elapsedSeconds}
                  </motion.span>
                </AnimatePresence>
                <span>초</span>
              </span>
            </div>
          )}
          {status === 'completed' && (
            <button
              type="button"
              onClick={handleNavigateToResult}
              className="glass animate-pop-in flex max-w-80 items-center gap-3 rounded-2xl px-5 py-3 shadow-lg transition-transform active:scale-95"
            >
              <div className="flex size-6 items-center justify-center rounded-full bg-emerald-500/20">
                <Check className="size-3 text-emerald-400" strokeWidth={2.5} aria-hidden="true" />
              </div>
              <div className="text-left">
                <p className="typo-label font-semibold text-foreground">풀이가 완성됐어요</p>
                <p className="typo-footnote text-muted-foreground">눌러서 풀이 확인하기</p>
              </div>
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
